import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTree } from '@fluidframework/react/alpha';
import { Add, Check, Close, Delete, Edit } from '@mui/icons-material';
import { Box, IconButton, TextField as MuiTextField, Table, TableBody, TableCell, TableRow, Typography, } from '@mui/material';
import React from 'react';
export function EditablePhysicalAttributes(props) {
    const { map, canEdit = false, emptyMessage = 'No entries.' } = props;
    useTree(map);
    const [editingKey, setEditingKey] = React.useState(undefined);
    const [editValue, setEditValue] = React.useState('');
    const [showAddForm, setShowAddForm] = React.useState(false);
    const entries = [...map.entries()];
    const hasEntries = entries.length > 0;
    const handleStartEdit = React.useCallback((key, value) => {
        if (canEdit) {
            setEditingKey(key);
            setEditValue(String(value));
        }
    }, [canEdit]);
    const handleSaveEdit = React.useCallback(() => {
        if (editingKey && editValue.trim()) {
            const parsedValue = parseValue(editValue.trim());
            map.set(editingKey, parsedValue);
        }
        setEditingKey(undefined);
        setEditValue('');
    }, [editingKey, editValue, map]);
    const handleCancelEdit = React.useCallback(() => {
        setEditingKey(undefined);
        setEditValue('');
    }, []);
    const handleDelete = React.useCallback((key) => {
        map.delete(key);
    }, [map]);
    const handleAdd = React.useCallback((key, value) => {
        if (key.trim() && value.trim()) {
            const parsedValue = parseValue(value.trim());
            map.set(key.trim(), parsedValue);
        }
        setShowAddForm(false);
    }, [map]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            handleCancelEdit();
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            handleSaveEdit();
        }
    }, [handleCancelEdit, handleSaveEdit]);
    if (!canEdit) {
        if (!hasEntries) {
            return (_jsx(Typography, { variant: "body2", color: "text.secondary", children: emptyMessage }));
        }
        return (_jsx(Table, { size: "small", children: _jsx(TableBody, { children: entries.map(([key, value]) => (_jsxs(TableRow, { children: [_jsx(TableCell, { component: "th", scope: "row", sx: { fontWeight: 'bold', width: '40%', borderBottom: 'none' }, children: key }), _jsx(TableCell, { sx: { borderBottom: 'none' }, children: String(value) })] }, key))) }) }));
    }
    return (_jsxs(Box, { children: [hasEntries ? (_jsx(Table, { size: "small", children: _jsx(TableBody, { children: entries.map(([key, value]) => (_jsxs(TableRow, { children: [_jsx(TableCell, { component: "th", scope: "row", sx: { fontWeight: 'bold', width: '35%', borderBottom: 'none' }, children: key }), _jsx(TableCell, { sx: { borderBottom: 'none', width: '45%' }, children: editingKey === key ? (_jsx(MuiTextField, { size: "small", value: editValue, onChange: (e) => setEditValue(e.target.value), onKeyDown: handleKeyDown, autoFocus: true, fullWidth: true })) : (_jsx(Box, { sx: {
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderRadius: 1,
                                        },
                                        padding: 0.5,
                                        borderRadius: 1,
                                    }, onClick: () => handleStartEdit(key, value), children: String(value) })) }), _jsx(TableCell, { sx: { borderBottom: 'none', width: '20%' }, align: "right", children: editingKey === key ? (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: "small", onClick: handleSaveEdit, color: "primary", "aria-label": "save", children: _jsx(Check, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: handleCancelEdit, "aria-label": "cancel", children: _jsx(Close, { fontSize: "small" }) })] })) : (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: "small", onClick: () => handleStartEdit(key, value), "aria-label": "edit", children: _jsx(Edit, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: () => handleDelete(key), color: "error", "aria-label": "delete", children: _jsx(Delete, { fontSize: "small" }) })] })) })] }, key))) }) })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: emptyMessage })), !showAddForm && (_jsx(IconButton, { onClick: () => setShowAddForm(true), "aria-label": "add entry", children: _jsx(Add, {}) })), showAddForm && (_jsx(KeyValueAddForm, { onAdd: handleAdd, onClose: () => setShowAddForm(false) }))] }));
}
function parseValue(value) {
    if (value.toLowerCase() === 'true')
        return true;
    if (value.toLowerCase() === 'false')
        return false;
    const num = Number(value);
    if (!isNaN(num) && value.trim() !== '') {
        return num;
    }
    return value;
}
function KeyValueAddForm(props) {
    const { onAdd, onClose } = props;
    const [key, setKey] = React.useState('');
    const [value, setValue] = React.useState('');
    const handleAdd = React.useCallback(() => {
        onAdd(key, value);
        setKey('');
        setValue('');
    }, [key, value, onAdd]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            handleAdd();
        }
    }, [onClose, handleAdd]);
    const canAdd = key.trim() && value.trim();
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mt: 1 }, children: [_jsx(MuiTextField, { size: "small", value: key, onChange: (e) => setKey(e.target.value), onKeyDown: handleKeyDown, placeholder: "Attribute", autoFocus: true, sx: { width: '35%' } }), _jsx(MuiTextField, { size: "small", value: value, onChange: (e) => setValue(e.target.value), onKeyDown: handleKeyDown, placeholder: "Value", sx: { width: '45%' } }), _jsx(IconButton, { size: "small", onClick: handleAdd, color: "primary", disabled: !canAdd, "aria-label": "add", children: _jsx(Add, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: onClose, "aria-label": "cancel", children: _jsx(Close, { fontSize: "small" }) })] }));
}
//# sourceMappingURL=EditablePhysicalAttributes.js.map